import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Eraser, Save, PenLine, CheckCircle2, FileText } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COMPANY_INFO = {
  name: 'NJK Logistics Pte Ltd',
  address: '212A Punggol Walk #12-741 Singapore 821212',
  phone: '98366186',
  email: 'inquiry@njklogistics.com',
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-SG', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

export default function PublicSignQuotation() {
  const { token } = useParams();
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState(null);
  const padRef = useRef(null);
  const [name, setName] = useState('');

  const fetchQuotation = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/public/quotations/sign/${token}`);
      setQuotation(res.data);
      setName(res.data.customer_signature_name || '');
    } catch (e) {
      setError(e.response?.data?.detail || 'This signing link is invalid or has been revoked.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSubmit = async () => {
    if (!padRef.current || padRef.current.isEmpty()) {
      toast.error('Please sign before submitting');
      return;
    }
    if (!name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    setSubmitting(true);
    try {
      let dataUrl;
      try {
        dataUrl = padRef.current.getTrimmedCanvas().toDataURL('image/png');
      } catch (err) {
        try {
          dataUrl = padRef.current.toDataURL('image/png');
        } catch (err2) {
          toast.error('Could not capture signature. Please clear and try again.');
          console.error('Signature capture failed:', err, err2);
          setSubmitting(false);
          return;
        }
      }
      if (!dataUrl || dataUrl.length < 100) {
        toast.error('Signature appears empty. Please try again.');
        setSubmitting(false);
        return;
      }
      await axios.patch(`${BACKEND_URL}/api/public/quotations/sign/${token}`, {
        customer_signature: dataUrl,
        customer_signature_name: name.trim(),
        terms_accepted: termsAccepted,
      });
      toast.success('Acknowledgement recorded. Signed copy emailed.', { duration: 5000 });
      await fetchQuotation();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit signature');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Loading quotation...</p>
      </div>
    );
  }

  if (error || !quotation) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <h2 className="text-xl font-bold text-red-600 mb-2">Link Unavailable</h2>
            <p className="text-gray-600">{error || 'This signing link is not valid.'}</p>
            <p className="text-sm text-gray-500 mt-4">Please contact NJK Logistics at {COMPANY_INFO.phone}.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const alreadySigned = !!quotation.customer_signature;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" data-testid="public-sign-quotation-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-blue-600">{COMPANY_INFO.name}</h1>
          <p className="text-sm text-gray-500">{COMPANY_INFO.address}</p>
          <p className="text-sm text-gray-500">Tel: {COMPANY_INFO.phone} · {COMPANY_INFO.email}</p>
        </div>

        {/* Quotation summary */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <FileText className="text-blue-600" /> Quotation
                </h2>
                <p className="text-2xl font-bold text-blue-600 mt-1">{quotation.document_number}</p>
              </div>
              {alreadySigned && (
                <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
                  ✓ Acknowledged
                </span>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-4 text-sm mb-4 border-t pt-4">
              <div>
                <p className="text-gray-500 mb-1">For</p>
                <p className="font-semibold">{quotation.company_name || quotation.customer_name}</p>
                {quotation.company_name && <p>Attn: {quotation.customer_name}</p>}
              </div>
              <div>
                <p className="text-gray-500 mb-1">Valid Until</p>
                <p className="font-semibold">
                  {quotation.validity_date ? new Date(quotation.validity_date).toLocaleDateString('en-SG', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                </p>
              </div>
            </div>

            {/* Line items */}
            <table className="w-full text-sm border-collapse mt-2">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border p-2 text-left">Description</th>
                  <th className="border p-2 text-center w-16">Qty</th>
                  <th className="border p-2 text-center w-16">Hrs</th>
                  <th className="border p-2 text-right w-24">Unit</th>
                  <th className="border p-2 text-right w-24">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(quotation.line_items || []).map((it, i) => (
                  <tr key={`q-li-${i}`}>
                    <td className="border p-2">{it.description}</td>
                    <td className="border p-2 text-center">{it.quantity}</td>
                    <td className="border p-2 text-center">{it.hours || '-'}</td>
                    <td className="border p-2 text-right">${(it.unit_price || 0).toFixed(2)}</td>
                    <td className="border p-2 text-right">${(it.amount || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs italic text-gray-500 mt-2">All rates quoted are on a per-job basis.</p>

            {quotation.notes && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-xs text-gray-500 mb-1">Notes</p>
                <p className="text-sm whitespace-pre-line">{quotation.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Signature */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
              <PenLine size={20} className="text-blue-600" /> Customer Acknowledgement
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              By signing below, you acknowledge and accept the terms of the above quotation.
            </p>
            {(quotation.terms_included || quotation.quote_type === 'container') && (
              <p className="text-xs text-gray-500 mb-4 -mt-2" data-testid="terms-notice">
                This quotation is subject to our{' '}
                <a href="/terms" target="_blank" rel="noreferrer" className="text-blue-600 underline font-medium">
                  Terms &amp; Conditions
                </a>{' '}
                (also attached in the quotation PDF).
              </p>
            )}

            {alreadySigned ? (
              <div className="border border-green-200 bg-green-50/40 rounded-md p-4">
                <div className="flex items-center gap-2 mb-3 text-green-700">
                  <CheckCircle2 size={20} />
                  <span className="font-semibold">Signed successfully</span>
                </div>
                <div className="bg-white border rounded p-2 mb-3 flex justify-center">
                  <img src={quotation.customer_signature} alt="Signature" className="max-h-24 object-contain" />
                </div>
                <p className="text-sm text-gray-700"><span className="text-gray-500">Name:</span> <strong>{quotation.customer_signature_name}</strong></p>
                <p className="text-sm text-gray-700"><span className="text-gray-500">Signed:</span> {fmtDate(quotation.customer_signed_at)}</p>
                <p className="text-xs text-gray-500 mt-3">A signed copy has been emailed to {quotation.customer_email}.</p>
              </div>
            ) : (
              <>
                {(quotation.terms_included || quotation.quote_type === 'container') && (
                  <label className="flex items-start gap-2.5 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md cursor-pointer" data-testid="terms-accept-label">
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      className="mt-0.5 h-4 w-4 accent-blue-600"
                      data-testid="terms-accept-checkbox"
                    />
                    <span className="text-sm text-gray-700">
                      I have read and agree to the{' '}
                      <a href="/terms" target="_blank" rel="noreferrer" className="text-blue-600 underline font-medium">
                        Terms &amp; Conditions
                      </a>{' '}
                      governing this quotation. *
                    </span>
                  </label>
                )}
                <Label className="text-sm">Name *</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Full name"
                  className="mt-1 mb-3"
                  data-testid="quotation-sign-name"
                />
                <Label className="text-sm">Sign in the box</Label>
                <div className="border-2 border-gray-300 rounded-md bg-white mt-1 mb-3 touch-none">
                  <SignatureCanvas
                    ref={padRef}
                    penColor="#1f2937"
                    clearOnResize={false}
                    canvasProps={{ className: 'w-full h-36 rounded-md', 'data-testid': 'quotation-sign-pad' }}
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={() => padRef.current?.clear()} disabled={submitting}>
                    <Eraser size={14} className="mr-1" /> Clear
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSubmit}
                    disabled={submitting || ((quotation.terms_included || quotation.quote_type === 'container') && !termsAccepted)}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="quotation-sign-submit"
                  >
                    <Save size={14} className="mr-1" /> {submitting ? 'Submitting...' : 'Acknowledge & sign'}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-gray-400 mt-8">Powered by NJK Logistics Pte Ltd</p>
      </div>
    </div>
  );
}
